import { type NestClientDiagnostics, nestClient } from "../nest/client"
import { getPlatformAdapter } from "./adapter"
import {
    type LiveStatusAdapter,
    type LiveStatusAvailability,
    type LiveStatusLifecycle,
    type LiveStatusReconciliation,
    getLiveStatusAdapter,
} from "./liveStatus"
import { type MobileLiveStatusPolicy, getMobileLiveStatusPolicy } from "./mobilePolicy"
import { type PushEnrollmentDiagnostics, getActivePushEnrollmentDiagnostics } from "./pushEnrollment"
import type { NotificationPermissionState, PlatformAdapter, PlatformInfo, PlatformResult } from "./types"

export type MobileDiagnosticPermission = NotificationPermissionState | "unavailable" | "failed"

export interface MobileDiagnosticsSnapshot {
    generatedAt: string
    platform: PlatformInfo
    connection: NestClientDiagnostics
    lastSuccessfulReconciliationAt: string | null
    push: PushEnrollmentDiagnostics & {
        permission: MobileDiagnosticPermission
        providerTokenAgeSeconds: number | null
    }
    liveStatus: {
        state: "ready" | "partial" | "unavailable"
        availability: LiveStatusAvailability | null
        monitoringEnabled: boolean | null
        running: boolean | null
        lifecycle: LiveStatusLifecycle | null
        remotePolicy: Pick<MobileLiveStatusPolicy, "state" | "liveStatusEnabled" | "reason" | "expiresAt">
    }
}

export interface MobileDiagnosticsSources {
    platform: Pick<PlatformAdapter, "kind" | "getInfo" | "getNotificationPermission">
    liveStatus: Pick<LiveStatusAdapter, "getAvailability" | "getMonitoringEnabled" | "reconcile">
    connection(): NestClientDiagnostics
    push(nodeId: string | null): Promise<PushEnrollmentDiagnostics>
    policy(): MobileLiveStatusPolicy
    now(): Date
}

type DeviceLiveStatusDiagnostics = Omit<MobileDiagnosticsSnapshot["liveStatus"], "remotePolicy">

let lastSuccessfulReconciliationAt: string | null = null

export function recordMobileReconciliationSuccess(at: Date = new Date()): void {
    lastSuccessfulReconciliationAt = at.toISOString()
}

export function resetMobileDiagnosticsForTests(): void {
    lastSuccessfulReconciliationAt = null
}

export async function collectMobileDiagnostics(): Promise<MobileDiagnosticsSnapshot> {
    const platform = getPlatformAdapter()
    return collectMobileDiagnosticsFrom({
        platform,
        liveStatus: getLiveStatusAdapter(platform.kind),
        connection: () => nestClient.getDiagnostics(),
        push: getActivePushEnrollmentDiagnostics,
        policy: getMobileLiveStatusPolicy,
        now: () => new Date(),
    })
}

export async function collectMobileDiagnosticsFrom(sources: MobileDiagnosticsSources): Promise<MobileDiagnosticsSnapshot> {
    const connection = sources.connection()
    const [platform, permission, availability, monitoring, reconciliation, push] = await Promise.all([
        safePlatformInfo(sources.platform),
        safePlatformResult(() => sources.platform.getNotificationPermission()),
        safePlatformResult(() => sources.liveStatus.getAvailability()),
        safePlatformResult(() => sources.liveStatus.getMonitoringEnabled()),
        safePlatformResult(() => sources.liveStatus.reconcile()),
        safePushDiagnostics(() => sources.push(connection.nodeId)),
    ])
    const now = sources.now()

    return {
        generatedAt: now.toISOString(),
        platform,
        connection,
        lastSuccessfulReconciliationAt,
        push: {
            ...push,
            permission: diagnosticPermission(permission),
            providerTokenAgeSeconds: timestampAgeSeconds(push.providerTokenUpdatedAt, now),
        },
        liveStatus: { ...liveStatusDiagnostics(availability, monitoring, reconciliation), remotePolicy: diagnosticPolicy(safePolicy(sources.policy)) },
    }
}

function safePolicy(source: () => MobileLiveStatusPolicy): MobileLiveStatusPolicy {
    try {
        return source()
    } catch {
        return {
            state: "unavailable",
            liveStatusEnabled: false,
            reason: "policy-unavailable",
            platform: null,
            appVersion: null,
            appBuild: null,
            expiresAt: null,
        }
    }
}

function diagnosticPolicy(policy: MobileLiveStatusPolicy): MobileDiagnosticsSnapshot["liveStatus"]["remotePolicy"] {
    return {
        state: policy.state,
        liveStatusEnabled: policy.liveStatusEnabled,
        reason: policy.reason,
        expiresAt: policy.expiresAt,
    }
}

async function safePlatformInfo(platform: MobileDiagnosticsSources["platform"]): Promise<PlatformInfo> {
    try {
        return await platform.getInfo()
    } catch {
        return { kind: platform.kind, native: platform.kind === "ios" || platform.kind === "android" }
    }
}

async function safePlatformResult<T>(operation: () => Promise<PlatformResult<T>>): Promise<PlatformResult<T>> {
    try {
        return await operation()
    } catch {
        return { status: "failed", message: "Diagnostic query failed" }
    }
}

async function safePushDiagnostics(operation: () => Promise<PushEnrollmentDiagnostics>): Promise<PushEnrollmentDiagnostics> {
    try {
        return await operation()
    } catch {
        return {
            state: "failed",
            pendingCount: 0,
            enrolledCount: 0,
            installationIdSuffix: null,
            providerTokenUpdatedAt: null,
        }
    }
}

function diagnosticPermission(result: PlatformResult<NotificationPermissionState>): MobileDiagnosticPermission {
    if (result.status === "ok") return result.value
    if (result.status === "denied") return "denied"
    if (result.status === "unavailable") return "unavailable"
    return "failed"
}

function liveStatusDiagnostics(
    availability: PlatformResult<LiveStatusAvailability>,
    monitoring: PlatformResult<boolean>,
    reconciliation: PlatformResult<LiveStatusReconciliation>
): DeviceLiveStatusDiagnostics {
    const available = availability.status === "ok" ? availability.value : null
    const monitoringEnabled = monitoring.status === "ok" ? monitoring.value : null
    const reconciled = reconciliation.status === "ok" ? reconciliation.value : null
    const unavailable = availability.status === "unavailable" || (available !== null && !available.supported)
    const partial = availability.status !== "ok" || monitoring.status !== "ok" || reconciliation.status !== "ok"
    return {
        state: unavailable ? "unavailable" : partial ? "partial" : "ready",
        availability: available,
        monitoringEnabled,
        running: reconciled?.running ?? null,
        lifecycle: reconciled?.lifecycle ?? null,
    }
}

function timestampAgeSeconds(timestamp: string | null, now: Date): number | null {
    if (!timestamp) return null
    const updatedAt = Date.parse(timestamp)
    if (!Number.isFinite(updatedAt)) return null
    return Math.max(0, Math.floor((now.getTime() - updatedAt) / 1_000))
}
